/**
 *
 * GSTR-9C New Save Tab Component
 *
 */

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@material-ui/core";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import moment from "moment";
import download from "images/download.svg";
import { setSelectedSave, setSelectedRowSave, downloadReport } from "./actions";
import "../style.css";

const SaveTab = (props) => {
  const dispatch = useDispatch();

  // State for error dialog
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    message: "",
    title: "Error Details"
  });

  // Redux selectors
  const { saveTableData, loaderStatus, selectedSave, selectedRowSave } =
    useSelector((state) => state.gstr9CNewSaveReducer || {});

  // Handle checkbox selection methods
  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      dispatch(setSelectedSave([...selectedSave, row.uniqueId]));
      dispatch(setSelectedRowSave([...selectedRowSave, row]));
    } else {
      const updatedSelection = selectedSave.filter((id) => id !== row.uniqueId);
      dispatch(setSelectedSave(updatedSelection));

      const updatedRows = selectedRowSave.filter((r) => r.uniqueId !== row.uniqueId);
      dispatch(setSelectedRowSave(updatedRows));
    }
  };

  const handleOnSelectAll = (isSelect, rows) => {
    const ids = rows.map((r) => r.uniqueId);
    if (isSelect) {
      dispatch(setSelectedSave(ids));
      dispatch(setSelectedRowSave(rows));
    } else {
      dispatch(setSelectedSave([]));
      dispatch(setSelectedRowSave([]));
    }
  };

  const handleDownload = (row) => {
    dispatch(downloadReport(row));
  };

  // Handle error dialog
  const handleErrorClick = (row) => {
    const errorMessage = row?.errorMsg?.message || "No error details available";
    setErrorDialog({
      open: true,
      message: errorMessage,
      title: "Error Details"
    });
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog({ ...errorDialog, open: false });
  };

  // Checkbox row selection configuration
  const selectRow = {
    mode: "checkbox",
    selected: selectedSave,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
    headerColumnStyle: {
      width: "3%",
      verticalAlign: "text-top",
    },
    selectColumnStyle: {
      width: "3%",
      textAlign: "center",
    },
  };

  const statusFormatter = (cell, row) => {
    const status = row?.status;
    const upperStatus = status?.toUpperCase();

    // Determine background color based on status
    let bgColor = "gray"; // Default gray (for unknown or neutral statuses)

    if (upperStatus === "COMPLETED")
      bgColor = "green"; // Green (success/completed)
    else if (status === null || status === '')
      bgColor = "orange"; // Yellow (in-progress/null status)
    else if (upperStatus === "FAILED") bgColor = "red"; // Red (failure/error)

    const showText = status === null || status === '' ? "Pending" : status;

    return (
      <div
      
      className="circle-div"
      style={{
        cursor: upperStatus === "FAILED" ? "pointer" : "default",
        color: bgColor,
      }}
      onClick={() => {
        if (upperStatus === "FAILED") {
        handleErrorClick(row);
        }
      }}
      >
      {showText}
      {/* <div
        className="circle-span"
        style={{
        marginTop: "3px",
        marginLeft: "8px",
        backgroundColor: bgColor,
        cursor: upperStatus === "FAILED" ? "pointer" : "default",
        }}
      ></div> */}
      </div>
    );
  };

  // Clear selected states on component unmount
  useEffect(() => {
    return () => {
      dispatch(setSelectedSave([]));
      dispatch(setSelectedRowSave([]));
    };
  }, [dispatch]);  // Table columns configuration
  const columns = [
    {
      dataField: "gstin",
      text: "GSTIN",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "24.25%",
      },
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
        fontWeight: "bold",
      },
    },
    {
      dataField: "created_date",
      text: "Date & Time",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "24.25%",
      },
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
      },
      formatter: (cell) => {
        return cell ? moment(cell).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    {
      dataField: "status",
      text: "Status",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "24.25%",
      },
      formatter: statusFormatter,
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
      },
    },

    {
      dataField: "errorReason",
      text: "Download",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "24.25%",
      },
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
      },
      formatter: (cell, row, rowIndex, formatExtraData) => {
        return (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <div>
              <img
                src={download}
                alt="download"
                style={{ width: "20px", height: "22px", cursor: "pointer" }}
                onClick={() => handleDownload(row)}
              />
            </div>
          </div>
        );
      },
    },
  ];



  return (
    <div style={{ padding: "20px" }}>
      {/* Bootstrap Table Section */}
      {saveTableData && saveTableData.length > 0 ? (
        <>
          <div style={{ marginTop: "20px" }}>
            <BootstrapTable
              keyField="uniqueId"
              data={saveTableData || []}
              columns={columns}
              //   pagination={paginationFactory(paginationOptions)}
              selectRow={selectRow}
              loading={loaderStatus}
              noDataIndication={() => (
                <div
                  style={{
                    textAlign: "center",
                    padding: "50px",
                    fontFamily: "GT-Walsheim-medium",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  No save data available
                </div>
              )}
              rowStyle={{
                backgroundColor: "#fff",
              }}
              striped
              hover
            />
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: '50px 20px',
              fontFamily: 'GT-Walsheim-medium',
              fontSize: '16px',
              color: '#666',
              minHeight: '200px',
              width: '100%',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
            }}
          >
            No save data available
          </div>
        </>
      )}

      {/* Error Dialog */}
      <Dialog
        open={errorDialog.open}
        onClose={handleCloseErrorDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            fontFamily: "GT-Walsheim-medium",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {errorDialog.title}
        </DialogTitle>
        <DialogContent
          style={{
            padding: "20px",
            backgroundColor: "#fff",
          }}
        >
          <Typography
            style={
              {
                fontFamily: "GT-Walsheim-medium",
                fontSize: "14px",
                color: "#333",
                lineHeight: "1.5",
                wordBreak: "break-word",
              }
            }
          >
            {errorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            padding: "10px 20px 20px",
            backgroundColor: "#fff",
          }}
        >
          <Button
            onClick={handleCloseErrorDialog}
            style={{
              backgroundColor: "#4f2d7f",
              color: "white",
              fontFamily: "GT-Walsheim-medium",
              fontSize: "14px",
              textTransform: "capitalize",
              padding: "8px 20px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SaveTab;
